import React, { PropsWithChildren, useEffect, useState } from 'react';
import { CSSObject } from '@core-ui/react-stitch';
import isRailStuck from './is-rail-stuck';

const creamChevron = `url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='6' viewBox='0 0 9 6'%3E%3Cpath d='M.003 1.533L1.118.529l3.385 3.047L7.89.529l1.114 1.004-4.5 4.05z' style='fill:%23f6f5f2;fill-rule:evenodd' /%3E%3C/svg%3E")`;
const blackChevron = `url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='6' viewBox='0 0 9 6'%3E%3Cpath d='M.003 1.533L1.118.529l3.385 3.047L7.89.529l1.114 1.004-4.5 4.05z' style='fill:%23000000;fill-rule:evenodd' /%3E%3C/svg%3E")`;

const chevronBaseStyles: CSSObject = {
  width: '1.5rem',
  height: '2rem',
  display: 'block',
  position: 'absolute',
  right: '1px',
  top: '1px',
  backgroundPosition: '2px 18px',
};

const creamTextOpaqueBlackBackground: CSSObject = {
  backgroundColor: '#00000088',
  color: '#f6f5f2',
  '&::before': {
    content: '""',
    background: creamChevron,
    backgroundRepeat: 'no-repeat',
    ...chevronBaseStyles,
  },
};

const creamTextFullBlackBackground: CSSObject = {
  backgroundColor: '#000000',
  color: '#f6f5f2',
  '&::before': {
    content: '""',
    background: creamChevron,
    backgroundRepeat: 'no-repeat',
    ...chevronBaseStyles,
  },
};

const creamTextNoBackground: CSSObject = {
  backgroundColor: 'none',
  color: '#f6f5f2',
  '&::before': {
    content: '""',
    background: creamChevron,
    backgroundRepeat: 'no-repeat',
    ...chevronBaseStyles,
  },
};

const blackTextNoBackground: CSSObject = {
  backgroundColor: 'none',
  color: '#000000',
  '&::before': {
    content: '""',
    background: blackChevron,
    backgroundRepeat: 'no-repeat',
    ...chevronBaseStyles,
  },
};

const StickyRailWrapper = ({
  stickyOffset,
  hasMarketingDataEBB,
  children,
}: PropsWithChildren<{
  stickyOffset: number;
  hasMarketingDataEBB: boolean;
}>) => {
  const [isStuck, setIsStuck] = useState(false);
  const railRef = React.useRef<HTMLDivElement>(null);

  const brColorChangeStyles: CSSObject = {
    '& select': {
      backgroundImage:
        isStuck || hasMarketingDataEBB ? creamChevron : blackChevron,
      '&:hover': {
        backgroundImage: creamChevron,
      },
    },
    '& button, select': {
      '&:hover': creamTextFullBlackBackground,
      ...(!isStuck && !hasMarketingDataEBB && blackTextNoBackground),
      ...(!isStuck && hasMarketingDataEBB && creamTextNoBackground),
      ...(isStuck && creamTextOpaqueBlackBackground),
      '&#filterButton': {
        '&::before': {
          content: 'none',
        },
      },
    },
  };

  useEffect(() => {
    const handleScroll = () => {
      if (isRailStuck(railRef, stickyOffset) && !isStuck) {
        setIsStuck(true);
      } else if (!isRailStuck(railRef, stickyOffset) && isStuck) {
        setIsStuck(false);
      }
    };
    // NOSONAR
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isStuck]);

  return (
    <div ref={railRef} css={brColorChangeStyles} data-testid="sticky-rail">
      {children}
    </div>
  );
};

export default StickyRailWrapper;
